import React from 'react';
import { BoltSlashIcon, DocumentIcon } from '@heroicons/react/24/outline';

import {
  AmsRefKind,
  KnowledgeGraphEntityType,
  useAmsBrowseFoldersQuery,
  useAmsBrowseActivitiesQuery,
  type AmsBrowseActivitiesQuery,
  type AmsBrowseFoldersQuery
} from '~/api/graphql';
import {
  type BrowseList,
  countOf,
  frameLabel,
  useCertificateList,
  useFileList,
  usePolicyList
} from './ams-browser-lists';
import {
  type Account,
  type AccountFilters,
  type AccountTab,
  type BrowseFile,
  type Capability,
  EXPIRY_PRESETS,
  FILE_TYPE_OPTIONS,
  fileDisplayName,
  type FileFilters,
  type IsSelected,
  isFiltered,
  joinParts,
  NO_ACCOUNT_FILTERS,
  NO_FILE_FILTERS,
  PAST_PRESETS,
  type Policy,
  policyLabel,
  STATUS_OPTIONS,
  type Toggle
} from './ams-browser-model';
import { type AmsRef, amsRefKey, entityTypeOfKind } from './ams-ref';
import { DateWindowChip, FilterBar, MultiSelectChip } from './filter-chips';
import { FolderPathChip } from './folder-path-chip';
import {
  ContextBackButton,
  ContextCrumb,
  ContextCrumbSeparator,
  ContextEntityRow,
  ContextTab,
  ContextTabs,
  DocumentListFrame,
  EmptyState,
  emptyStateIconClass,
  ListSpinner,
  LoadError,
  LoadMoreButton,
  PickerContextBar,
  PickerViewport,
  SearchRail,
  policyBadges
} from './picker-ui';

/** An account's policies, certificates and own files, one tab each. */
export function AccountView(props: {
  capability: Capability;
  account: Account;
  tab: AccountTab;
  onTab: (tab: AccountTab) => void;
  filters: AccountFilters;
  onFilters: (filters: AccountFilters) => void;
  onBack?: () => void;
  isSelected: IsSelected;
  onToggle: Toggle;
  onOpenPolicy: (policy: Policy) => void;
  recoveryHint?: string;
}) {
  const { capability, account, filters } = props;
  const system = capability.displayName;
  const certificatesShown = capability.pickableKinds.includes(AmsRefKind.Certificate);
  const filesShown = capability.filesBrowsable;
  const policies = usePolicyList(account, filters.policies);
  const certificates = useCertificateList(account, filters.certificates, !certificatesShown);
  const files = useFileList(account.ref, filters.files, !filesShown);
  const tab =
    (props.tab === 'certificates' && !certificatesShown) || (props.tab === 'files' && !filesShown)
      ? 'policies'
      : props.tab;
  const patch =
    <K extends keyof AccountFilters>(key: K) =>
    (change: Partial<AccountFilters[K]>) =>
      props.onFilters({ ...filters, [key]: { ...filters[key], ...change } });
  const pick = (ref: AmsRef, displayName: string) =>
    props.onToggle({ ref, displayName, capability, accountName: account.name });
  const ids = React.useId();
  const tabs: Array<{ tab: AccountTab; label: string; list: BrowseList<unknown>; shown: boolean }> =
    [
      { tab: 'policies', label: 'Policies', list: policies, shown: true },
      { tab: 'certificates', label: 'Certificates', list: certificates, shown: certificatesShown },
      { tab: 'files', label: 'Files', list: files, shown: filesShown }
    ];
  const setPolicies = patch('policies');
  const setCertificates = patch('certificates');

  return (
    <>
      <PickerContextBar>
        {props.onBack && (
          <ContextBackButton onClick={props.onBack} ariaLabel="Back to account search" />
        )}
        <ContextCrumb>{account.name}</ContextCrumb>
        <ContextTabs label="Account records">
          {tabs
            .filter((t) => t.shown)
            .map((t) => (
              <ContextTab
                key={t.tab}
                id={`${ids}-${t.tab}`}
                controls={`${ids}-panel`}
                label={t.label}
                count={countOf(t.list)}
                active={tab === t.tab}
                onClick={() => props.onTab(t.tab)}
              />
            ))}
        </ContextTabs>
      </PickerContextBar>
      <PickerViewport panelId={`${ids}-panel`} labelledBy={`${ids}-${tab}`}>
        {tab === 'policies' && (
          <>
            <FilterBar
              query={filters.policies.query}
              onQueryChange={(query) => setPolicies({ query })}
              placeholder="Search by policy number or carrier"
              ariaLabel="Search policies"
              onClear={
                isFiltered(filters.policies)
                  ? () => setPolicies(NO_ACCOUNT_FILTERS.policies)
                  : undefined
              }
            >
              <MultiSelectChip
                label="Status"
                options={STATUS_OPTIONS}
                values={filters.policies.statuses}
                onChange={(statuses) => setPolicies({ statuses })}
              />
              <DateWindowChip
                label="Expires"
                presets={EXPIRY_PRESETS}
                value={filters.policies.expires}
                onChange={(expires) => setPolicies({ expires })}
              />
            </FilterBar>
            <ListBody
              list={policies}
              noun="policies"
              system={system}
              filtered={isFiltered(filters.policies)}
              empty="No policies found for this account"
              emptyHint={props.recoveryHint}
              recoveryHint={props.recoveryHint}
            >
              {policies.items.map((policy) => {
                const label = policyLabel(policy);
                return (
                  <ContextEntityRow
                    key={amsRefKey(policy.ref)}
                    entityType={entityTypeOfKind(AmsRefKind.Policy)}
                    label={label}
                    secondary={joinParts(policy.carrier, policy.lineOfBusiness)}
                    badges={policyBadges({
                      status: policy.status,
                      expiresAt: policy.expirationDate
                    })}
                    hideTypeBadge
                    selectDisabled={!capability.pickableKinds.includes(AmsRefKind.Policy)}
                    selected={props.isSelected(policy.ref)}
                    onToggle={() => pick(policy.ref, label)}
                    ariaLabel={`Select policy ${label}`}
                    onNavigate={filesShown ? () => props.onOpenPolicy(policy) : undefined}
                    navigateLabel={`Open policy ${label}`}
                  />
                );
              })}
            </ListBody>
          </>
        )}

        {tab === 'certificates' && (
          <>
            <FilterBar
              query={filters.certificates.query}
              onQueryChange={(query) => setCertificates({ query })}
              placeholder="Search by title or holder"
              ariaLabel="Search certificates"
              onClear={
                isFiltered(filters.certificates)
                  ? () => setCertificates(NO_ACCOUNT_FILTERS.certificates)
                  : undefined
              }
            >
              <DateWindowChip
                label="Issued"
                presets={PAST_PRESETS}
                value={filters.certificates.issued}
                onChange={(issued) => setCertificates({ issued })}
              />
            </FilterBar>
            <ListBody
              list={certificates}
              noun="certificates"
              system={system}
              filtered={isFiltered(filters.certificates)}
              empty="No certificates found for this account"
            >
              {certificates.items.map((certificate) => {
                const label = certificate.title ?? certificate.holder ?? 'Certificate';
                return (
                  <ContextEntityRow
                    key={amsRefKey(certificate.ref)}
                    entityType={entityTypeOfKind(AmsRefKind.Certificate)}
                    label={label}
                    secondary={
                      certificate.holder && certificate.holder !== label
                        ? certificate.holder
                        : undefined
                    }
                    modifiedAt={certificate.issuedAt ?? undefined}
                    hideTypeBadge
                    selected={props.isSelected(certificate.ref)}
                    onToggle={() => pick(certificate.ref, label)}
                    ariaLabel={`Select certificate ${label}`}
                  />
                );
              })}
            </ListBody>
          </>
        )}

        {tab === 'files' && (
          <FileTab
            list={files}
            filters={filters.files}
            onFilters={(next) => props.onFilters({ ...filters, files: next })}
            system={system}
            empty="No files filed on this account"
            emptyHint="Files filed on a policy are listed under that policy."
            capability={capability}
            account={account}
            isSelected={props.isSelected}
            onToggle={props.onToggle}
          />
        )}
      </PickerViewport>
    </>
  );
}

/** One policy's files, under a single Files tab. */
export function PolicyView(props: {
  capability: Capability;
  account: Account;
  policy: Policy;
  filters: FileFilters;
  onFilters: (filters: FileFilters) => void;
  onBack: () => void;
  backLabel: string;
  onOpenAccount: () => void;
  isSelected: IsSelected;
  onToggle: Toggle;
}) {
  const { capability, account, policy } = props;
  const files = useFileList(policy.ref, props.filters, !capability.filesBrowsable);
  const ids = React.useId();
  return (
    <>
      <PickerContextBar>
        <ContextBackButton onClick={props.onBack} ariaLabel={props.backLabel} />
        <ContextCrumb onClick={props.onOpenAccount}>{account.name}</ContextCrumb>
        <ContextCrumbSeparator />
        <ContextCrumb>Policy #{policyLabel(policy)}</ContextCrumb>
        <ContextTabs label="Policy records">
          <ContextTab
            id={`${ids}-files`}
            controls={`${ids}-panel`}
            label="Files"
            count={countOf(files)}
            active
          />
        </ContextTabs>
      </PickerContextBar>
      <PickerViewport panelId={`${ids}-panel`} labelledBy={`${ids}-files`}>
        {capability.filesBrowsable ? (
          <FileTab
            list={files}
            filters={props.filters}
            onFilters={props.onFilters}
            system={capability.displayName}
            empty="No files in this policy"
            capability={capability}
            account={account}
            isSelected={props.isSelected}
            onToggle={props.onToggle}
          />
        ) : (
          <EmptyState
            icon={<BoltSlashIcon className={emptyStateIconClass} />}
            title="Document browsing isn't supported for this AMS"
            description="Attach files manually below to continue."
          />
        )}
      </PickerViewport>
    </>
  );
}

function FileTab(props: {
  list: BrowseList<BrowseFile>;
  filters: FileFilters;
  onFilters: (filters: FileFilters) => void;
  system: string;
  empty: string;
  emptyHint?: string;
  capability: Capability;
  account: Account;
  isSelected: IsSelected;
  onToggle: Toggle;
}) {
  const { list, filters, onFilters } = props;
  return (
    <>
      <FilterBar
        query={filters.query}
        onQueryChange={(query) => onFilters({ ...filters, query })}
        placeholder="Search by file name"
        ariaLabel="Search files"
        onClear={isFiltered(filters) ? () => onFilters(NO_FILE_FILTERS) : undefined}
      >
        <DateWindowChip
          label="Attached"
          presets={PAST_PRESETS}
          value={filters.attached}
          onChange={(attached) => onFilters({ ...filters, attached })}
        />
        <MultiSelectChip
          label="Type"
          options={FILE_TYPE_OPTIONS}
          values={filters.types}
          onChange={(types) => onFilters({ ...filters, types })}
        />
        <FolderPathChip
          value={filters.folder}
          onChange={(folder) => onFilters({ ...filters, folder })}
        />
      </FilterBar>
      <ListBody
        list={list}
        noun="files"
        system={props.system}
        filtered={isFiltered(filters)}
        empty={props.empty}
        emptyHint={props.emptyHint}
      >
        {list.items.map((file) => (
          <ContextEntityRow
            key={amsRefKey(file.ref)}
            entityType={entityTypeOfKind(AmsRefKind.File)}
            label={file.title}
            secondary={file.extension?.replace(/^\./, '').toUpperCase() || undefined}
            folderName={file.folderPath ?? undefined}
            modifiedAt={file.attachedAt ?? undefined}
            disabled={!file.selectable}
            disabledReason={file.unselectableReason ?? undefined}
            selected={props.isSelected(file.ref)}
            onToggle={() =>
              props.onToggle({
                ref: file.ref,
                displayName: fileDisplayName(file),
                capability: props.capability,
                accountName: props.account.name
              })
            }
            ariaLabel={`Select ${file.title}`}
          />
        ))}
      </ListBody>
    </>
  );
}

/** A tab's list in its frame: loading, failed, empty, or its rows and Load more. */
function ListBody(props: {
  list: BrowseList<unknown>;
  noun: string;
  system: string;
  filtered: boolean;
  empty: string;
  emptyHint?: string;
  recoveryHint?: string;
  children: React.ReactNode;
}) {
  const { list, noun } = props;
  if (list.failed) {
    return (
      <DocumentListFrame countLabel={null}>
        <div className="p-3">
          <LoadError>
            Couldn't load {noun} from {props.system}.{' '}
            {props.recoveryHint ?? 'Try again or attach files manually.'}
          </LoadError>
        </div>
      </DocumentListFrame>
    );
  }
  if (list.initialLoading) {
    return (
      <DocumentListFrame countLabel={null}>
        <ListSpinner label={`Loading ${noun}…`} />
      </DocumentListFrame>
    );
  }
  const loadMore = list.more && (
    <LoadMoreButton
      onClick={list.loadMore}
      loading={list.loadingMore}
      failed={list.loadMoreFailed}
    />
  );
  if (list.items.length === 0) {
    // A live read that filters after reading can end a page with nothing yet
    // and more still to read.
    const empty = list.more
      ? { title: `No ${noun} found yet`, description: 'Load more to keep looking.' }
      : props.filtered
        ? {
            title: `No ${noun} match these filters`,
            description: 'Try a different search, or clear the filters.'
          }
        : {
            title: props.empty,
            description: props.emptyHint ?? 'Attach files manually below if you need to.'
          };
    return (
      <DocumentListFrame countLabel={null}>
        <EmptyState icon={<DocumentIcon className={emptyStateIconClass} />} {...empty} />
        {loadMore}
      </DocumentListFrame>
    );
  }
  return (
    <DocumentListFrame countLabel={frameLabel(list, noun)}>
      <ul>{props.children}</ul>
      {loadMore}
    </DocumentListFrame>
  );
}

export function FolderView(props: {
  account: Account;
  selectedId?: string;
  onChoose: (folder: AmsBrowseFoldersQuery['amsBrowseFolders'][number] | null) => void;
}) {
  const result = useAmsBrowseFoldersQuery({
    variables: {
      input: {
        account: {
          integrationId: props.account.ref.integrationId,
          kind: AmsRefKind.Account,
          externalId: props.account.ref.externalId
        }
      }
    },
    fetchPolicy: 'network-only'
  });
  const [query, setQuery] = React.useState('');
  const folders = (result.data?.amsBrowseFolders ?? []).filter((folder) =>
    folder.displayName.toLowerCase().includes(query.trim().toLowerCase())
  );
  return (
    <div className="space-y-3">
      <SearchRail
        query={query}
        onQueryChange={setQuery}
        placeholder="Search folders"
        ariaLabel="Search folders"
      />
      {result.error ? (
        <LoadError>Couldn't load folders. Try again or use Client record.</LoadError>
      ) : result.loading ? (
        <ListSpinner label="Loading folders…" />
      ) : (
        <DocumentListFrame countLabel={`${folders.length} folders`}>
          <ContextEntityRow
            entityType={KnowledgeGraphEntityType.File}
            hideTypeBadge
            label="No folder"
            selected={!props.selectedId}
            onToggle={() => props.onChoose(null)}
            ariaLabel="Select no folder"
          />
          {folders.map((folder) => (
            <ContextEntityRow
              key={folder.ref.externalId}
              entityType={KnowledgeGraphEntityType.File}
              hideTypeBadge
              label={folder.displayName}
              selected={folder.ref.externalId === props.selectedId}
              onToggle={() => props.onChoose(folder)}
              ariaLabel={`Select folder ${folder.displayName}`}
            />
          ))}
        </DocumentListFrame>
      )}
    </div>
  );
}

export function ActivityView(props: {
  account: Account;
  selectedId?: string;
  onChoose: (activity: AmsBrowseActivitiesQuery['amsBrowseActivities'][number] | null) => void;
}) {
  const result = useAmsBrowseActivitiesQuery({
    variables: {
      input: {
        account: {
          integrationId: props.account.ref.integrationId,
          kind: AmsRefKind.Account,
          externalId: props.account.ref.externalId
        }
      }
    },
    fetchPolicy: 'network-only'
  });
  const [query, setQuery] = React.useState('');
  const activities = (result.data?.amsBrowseActivities ?? []).filter((activity) =>
    activity.displayName.toLowerCase().includes(query.trim().toLowerCase())
  );
  return (
    <div className="space-y-3">
      <SearchRail
        query={query}
        onQueryChange={setQuery}
        placeholder="Search recent activities"
        ariaLabel="Search recent activities"
      />
      {result.error ? (
        <LoadError>Couldn't load activities. Try again or use Client record.</LoadError>
      ) : result.loading ? (
        <ListSpinner label="Loading activities…" />
      ) : (
        <DocumentListFrame countLabel={`${activities.length} recent activities`}>
          <ContextEntityRow
            entityType={KnowledgeGraphEntityType.Activity}
            hideTypeBadge
            label="Client record"
            selected={!props.selectedId}
            onToggle={() => props.onChoose(null)}
            ariaLabel="Select no activity"
          />
          {activities.map((activity) => (
            <ContextEntityRow
              key={activity.ref.externalId}
              entityType={KnowledgeGraphEntityType.Activity}
              hideTypeBadge
              label={activity.displayName}
              selected={activity.ref.externalId === props.selectedId}
              onToggle={() => props.onChoose(activity)}
              ariaLabel={`Select activity ${activity.displayName}`}
            />
          ))}
        </DocumentListFrame>
      )}
    </div>
  );
}
